import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { axiosInstance, API } from '@/App';
import { useAttendanceGate } from '@/context/AttendanceContext';
import { useMyStrikes } from '@/hooks/useMyStrikes';
import { useTeamStrikes } from '@/hooks/useTeamStrikes';
import AttendanceHistory from '@/components/attendance/AttendanceHistory';
import TeamStrikesTable from '@/components/attendance/TeamStrikesTable';
import DailyRegularizationModal from '@/components/attendance/DailyRegularizationModal';
import { format, startOfWeek, addDays, isToday, isBefore, startOfDay } from 'date-fns';
import {
  Clock,
  MapPin,
  CheckCircle,
  AlertTriangle,
  Lock,
  Calendar,
  LogIn,
  LogOut,
  Sun,
  CloudSun,
  Loader2,
  ChevronDown,
  ChevronUp,
  FileEdit,
  Shield,
  XCircle,
  X,
  Camera,
} from 'lucide-react';

/**
 * Mark Attendance page — replaces the old MyRoster page.
 *
 * Shows attendance status (blocked / missed checkout / not checked in /
 * checked in / checked out) and lets the user check in/out via modals
 * rendered globally in App.js.
 *
 * Below the main card: attendance history (This Week / History tabs).
 */
export default function MyRoster() {
  // ----- context & user --------------------------------------------------
  const {
    isCheckedIn,
    checkedInLocation,
    checkInTime,
    hasPendingMissedCheckout: ctxHasPendingMissedCheckout,
    pendingMissedCheckoutRecord: ctxPendingMissedCheckoutRecord,
    strikeCount: ctxStrikeCount,
    isBlocked: ctxIsBlocked,
    setShowCheckInModal,
    openCheckOutModal,
    refreshStatus,
    dailyStatus,
  } = useAttendanceGate();

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // ----- strikes ----------------------------------------------------------
  const { strikes, loading: strikesLoading, refetch: refetchStrikes } = useMyStrikes();
  const { teamStrikes, loading: teamStrikesLoading } = useTeamStrikes();
  const isManager = currentUser.role === 'manager' || currentUser.role === 'admin';

  // ----- live clock (updates every second) --------------------------------
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // ----- duration counter (updates every minute) --------------------------
  const [duration, setDuration] = useState(null);
  useEffect(() => {
    if (!isCheckedIn || !checkInTime) {
      setDuration(null);
      return;
    }
    const calc = () => {
      const diff = Date.now() - new Date(checkInTime).getTime();
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      setDuration({ hours: h, minutes: m });
    };
    calc();
    const id = setInterval(calc, 60_000);
    return () => clearInterval(id);
  }, [isCheckedIn, checkInTime]);

  // ----- missed-checkout justification ------------------------------------
  const [justification, setJustification] = useState('');
  const [justificationLoading, setJustificationLoading] = useState(false);

  // Detect pending missed checkout from context
  const hasPendingMissedCheckout = ctxHasPendingMissedCheckout;
  const pendingMissedCheckoutRecord = ctxPendingMissedCheckoutRecord || dailyStatus?.pending_record || null;

  const submitJustification = async () => {
    if (!justification.trim()) {
      toast.error('Please provide a reason for the missed checkout.');
      return;
    }
    setJustificationLoading(true);
    try {
      await axiosInstance.post('/attendance/daily/missed-checkout-justification', {
        justification: justification.trim(),
        attendance_id: pendingMissedCheckoutRecord?.attendance_id || pendingMissedCheckoutRecord?._id,
      });
      toast.success('Justification submitted successfully.');
      setJustification('');
      // Refresh context so the page re-evaluates state
      window.dispatchEvent(new Event('user-changed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit justification.');
    } finally {
      setJustificationLoading(false);
    }
  };

  // ----- today's completed record (for State 5) --------------------------
  const [todayRecord, setTodayRecord] = useState(null);
  const [todayRecordLoading, setTodayRecordLoading] = useState(false);

  // Fetch today's record when not checked in and no active shift to detect "day complete"
  useEffect(() => {
    const fetchTodayRecord = async () => {
      if (isCheckedIn) {
        setTodayRecord(null);
        return;
      }
      setTodayRecordLoading(true);
      try {
        const today = format(new Date(), 'yyyy-MM-dd');
        const res = await axiosInstance.get('/attendance/daily/my-history', {
          params: { from_date: today, to_date: today, page_size: 1 },
        });
        const entries = res.data.entries || [];
        if (entries.length > 0 && entries[0].check_out_time) {
          setTodayRecord(entries[0]);
        } else {
          setTodayRecord(null);
        }
      } catch {
        setTodayRecord(null);
      } finally {
        setTodayRecordLoading(false);
      }
    };
    fetchTodayRecord();
  }, [isCheckedIn, dailyStatus]);

  // ----- weekly summary ---------------------------------------------------
  const [weeklySummary, setWeeklySummary] = useState([]);
  const [weeklyLoading, setWeeklyLoading] = useState(false);

  useEffect(() => {
    const fetchWeekly = async () => {
      setWeeklyLoading(true);
      try {
        const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
        const weekEnd = addDays(weekStart, 6);
        const res = await axiosInstance.get('/attendance/daily/my-history', {
          params: {
            from_date: format(weekStart, 'yyyy-MM-dd'),
            to_date: format(weekEnd, 'yyyy-MM-dd'),
            page_size: 50,
          },
        });
        setWeeklySummary(res.data.entries || []);
      } catch {
        setWeeklySummary([]);
      } finally {
        setWeeklyLoading(false);
      }
    };
    fetchWeekly();
  }, [isCheckedIn]); // refresh when check-in/out changes

  // ----- strikes section (collapsible) ------------------------------------
  const [strikesExpanded, setStrikesExpanded] = useState(false);

  // ----- active tab -------------------------------------------------------
  const [activeTab, setActiveTab] = useState('week');

  // ----- regularization state ---------------------------------------------
  const [regModalOpen, setRegModalOpen] = useState(false);
  const [regDate, setRegDate] = useState(null);
  const [myRegRequests, setMyRegRequests] = useState([]);
  const [regLoading, setRegLoading] = useState(false);

  // Fetch my regularization requests
  const fetchMyRegularizations = useCallback(async () => {
    setRegLoading(true);
    try {
      const res = await axiosInstance.get('/attendance/daily/regularization/my-requests');
      setMyRegRequests(res.data?.requests || res.data || []);
    } catch {
      setMyRegRequests([]);
    } finally {
      setRegLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyRegularizations();
  }, [fetchMyRegularizations]);

  // ----- BH0 (Billing Hour Zero) state ------------------------------------
  const [bh0Penalties, setBh0Penalties] = useState([]);
  const [bh0ActiveCount, setBh0ActiveCount] = useState(0);
  const [bh0Loading, setBh0Loading] = useState(false);
  const [bh0BannerDismissed, setBh0BannerDismissed] = useState(
    () => sessionStorage.getItem('bh0_banner_dismissed') === 'true'
  );
  const [bh0AppealingId, setBh0AppealingId] = useState(null);
  const [bh0AppealText, setBh0AppealText] = useState('');
  const [bh0AppealLoading, setBh0AppealLoading] = useState(false);
  const [bh0CardExpanded, setBh0CardExpanded] = useState(true);

  // Fetch BH0 penalties for current month
  const fetchBh0Penalties = useCallback(async () => {
    setBh0Loading(true);
    try {
      const month = format(new Date(), 'yyyy-MM');
      const res = await axiosInstance.get('/attendance/daily/bh0/my-penalties', {
        params: { month },
      });
      setBh0Penalties(res.data?.penalties || []);
      setBh0ActiveCount(res.data?.active_count || 0);
    } catch {
      setBh0Penalties([]);
      setBh0ActiveCount(0);
    } finally {
      setBh0Loading(false);
    }
  }, []);

  useEffect(() => {
    fetchBh0Penalties();
  }, [fetchBh0Penalties]);

  // BH0 lookup by date for weekly grid
  const bh0ByDate = useMemo(() => {
    const map = {};
    bh0Penalties.forEach((p) => {
      if (p.status !== 'reversed') {
        map[p.date] = p;
      }
    });
    return map;
  }, [bh0Penalties]);

  // BH0 banner dismiss handler
  const dismissBh0Banner = useCallback(() => {
    setBh0BannerDismissed(true);
    sessionStorage.setItem('bh0_banner_dismissed', 'true');
  }, []);

  // BH0 appeal submit
  const submitBh0Appeal = useCallback(async (bh0Id) => {
    if (bh0AppealText.trim().length < 10) {
      toast.error('Appeal reason must be at least 10 characters');
      return;
    }
    setBh0AppealLoading(true);
    try {
      await axiosInstance.post(`/attendance/daily/bh0/${bh0Id}/appeal`, {
        reason: bh0AppealText.trim(),
      });
      toast.success('Appeal submitted successfully. Admin will review.');
      setBh0AppealingId(null);
      setBh0AppealText('');
      fetchBh0Penalties();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit appeal');
    } finally {
      setBh0AppealLoading(false);
    }
  }, [bh0AppealText, fetchBh0Penalties]);

  // BH0 status badge helper
  const getBh0StatusBadge = useCallback((status) => {
    const cfg = {
      active: { bg: 'bg-red-100 text-red-700 border-red-300', label: 'Active' },
      appealed: { bg: 'bg-amber-100 text-amber-700 border-amber-300', label: 'Appealed' },
      appeal_upheld: { bg: 'bg-red-100 text-red-800 border-red-400', label: 'Upheld' },
      reversed: { bg: 'bg-green-100 text-green-700 border-green-300', label: 'Reversed' },
    };
    const c = cfg[status] || { bg: 'bg-gray-100 text-gray-600', label: status };
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${c.bg}`}>
        {c.label}
      </span>
    );
  }, []);

  // Ref for scrolling to BH0 card
  const bh0CardRef = useRef(null);

  // Map regularization requests by date for quick lookup
  const regByDate = useMemo(() => {
    const map = {};
    myRegRequests.forEach((r) => {
      map[r.date] = r;
    });
    return map;
  }, [myRegRequests]);

  // Helper: can this day be regularized?
  const canRegularize = useCallback((day, entry) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const todayStart = startOfDay(new Date());
    // Must be in the past, no pending request, and either no entry or a weekly_off entry
    return (
      isBefore(day, todayStart) &&
      (!entry || entry.status === 'weekly_off') &&
      !regByDate[dayStr]
    );
  }, [regByDate]);

  // Helper: get regularization status badge for a date
  const getRegBadge = useCallback((dayStr) => {
    const req = regByDate[dayStr];
    if (!req) return null;
    const statusConfig = {
      pending_manager: { bg: 'bg-amber-100 text-amber-700', label: 'Pending Manager' },
      pending_admin: { bg: 'bg-blue-100 text-blue-700', label: 'Pending Admin' },
      approved: { bg: 'bg-green-100 text-green-700', label: 'Regularized' },
      rejected: { bg: 'bg-red-100 text-red-700', label: 'Rejected' },
    };
    const cfg = statusConfig[req.status] || { bg: 'bg-gray-100 text-gray-600', label: req.status };
    return (
      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cfg.bg}`}>
        {cfg.label}
      </span>
    );
  }, [regByDate]);

  // ----- derived state ----------------------------------------------------
  const isBlocked = ctxIsBlocked || strikes?.is_locked === true;
  const strikeCount = ctxStrikeCount || strikes?.strike_count || 0;

  const isAfternoon = now.getHours() >= 13;
  const greeting = useMemo(() => {
    const h = now.getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  }, [now.getHours()]);

  const formattedDate = format(now, 'EEEE, MMMM d, yyyy');
  const formattedTime = format(now, 'hh:mm:ss a');

  // Format check-in time in IST display
  const checkInTimeDisplay = useMemo(() => {
    if (!checkInTime) return '';
    try {
      return format(new Date(checkInTime), 'hh:mm a');
    } catch {
      return '';
    }
  }, [checkInTime]);

  // ----- helpers for weekly view ------------------------------------------
  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, []);

  const getWeekDayEntry = useCallback(
    (day) => {
      const dayStr = format(day, 'yyyy-MM-dd');
      return weeklySummary.find((e) => {
        const entryDate = e.date || e.check_in_time?.split('T')[0];
        return entryDate === dayStr;
      });
    },
    [weeklySummary]
  );

  const getStatusColor = (entry, day) => {
    if (isToday(day)) return 'border-[#7C1D3E] bg-[#7C1D3E]/5';
    if (!entry) return 'border-gray-200 bg-gray-50';
    const status = entry.status;
    if (status === 'on_time') return 'border-green-300 bg-green-50';
    if (status === 'late') return 'border-amber-300 bg-amber-50';
    if (status === 'missed') return 'border-red-300 bg-red-50';
    if (status === 'weekly_off') return 'border-gray-300 bg-gray-100';
    if (status === 'half_day') return 'border-yellow-300 bg-yellow-50';
    if (status === 'early_checkout') return 'border-orange-300 bg-orange-50';
    return 'border-gray-200 bg-gray-50';
  };

  const getStatusBadge = (entry) => {
    if (!entry) return null;
    const s = entry.status;
    const styles = {
      on_time: 'bg-green-100 text-green-800',
      late: 'bg-amber-100 text-amber-800',
      missed: 'bg-red-100 text-red-800',
      weekly_off: 'bg-gray-100 text-gray-600',
      half_day: 'bg-yellow-100 text-yellow-800',
      early_checkout: 'bg-orange-100 text-orange-800',
      regularized: 'bg-blue-100 text-blue-800',
    };
    const labels = {
      on_time: 'Present',
      late: 'Late',
      missed: 'Absent',
      weekly_off: 'Off',
      half_day: 'Half Day',
      early_checkout: 'Early',
      regularized: 'Regularized',
    };
    return (
      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${styles[s] || 'bg-gray-100 text-gray-600'}`}>
        {labels[s] || s || 'N/A'}
      </span>
    );
  };

  // Format completed record hours
  const formatHoursWorked = (record) => {
    if (!record) return '';
    if (record.hours_worked != null) {
      const h = Math.floor(record.hours_worked);
      const m = Math.round((record.hours_worked - h) * 60);
      return `${h} hours ${m} minutes`;
    }
    if (record.check_in_time && record.check_out_time) {
      const diff = new Date(record.check_out_time) - new Date(record.check_in_time);
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      return `${h} hours ${m} minutes`;
    }
    return '';
  };

  const formatRecordTime = (isoStr) => {
    if (!isoStr) return '-';
    try {
      return format(new Date(isoStr), 'hh:mm a');
    } catch {
      return '-';
    }
  };

  // ======================================================================
  // RENDER
  // ======================================================================

  return (
    <div className="p-4 md:p-6 space-y-4">

      {/* ================================================================
          BH0 BANNER (dismissible red banner at top)
          ================================================================ */}
      {bh0ActiveCount > 0 && !bh0BannerDismissed && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <p className="text-sm font-medium text-red-800">
              {bh0ActiveCount} day{bh0ActiveCount !== 1 ? 's' : ''} marked Billing Hour Zero this month
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => bh0CardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="text-xs font-medium text-red-700 hover:text-red-900 underline"
            >
              View Details
            </button>
            <button
              onClick={dismissBh0Banner}
              className="p-1 hover:bg-red-100 rounded-lg transition-colors"
              title="Dismiss"
            >
              <X className="h-4 w-4 text-red-500" />
            </button>
          </div>
        </div>
      )}

      {/* ================================================================
          MAIN STATUS CARD
          ================================================================ */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-[#7C1D3E]" />
            Mark Attendance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* ---------- STATE 1: Blocked (3+ strikes) ---------- */}
          {isBlocked && (
            <div className="text-center py-8 space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <Lock className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-red-800">Account Locked</h2>
              <p className="text-red-600 max-w-md mx-auto">
                You have 3 or more missed checkout strikes.
                Contact your admin to unlock your account.
              </p>
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <span className="text-sm font-medium text-red-700">
                  Strike count: {strikeCount}
                </span>
              </div>
            </div>
          )}

          {/* ---------- STATE 2: Pending Missed Checkout ---------- */}
          {!isBlocked && hasPendingMissedCheckout && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900">
                    Missed Checkout — Justification Required
                  </h3>
                  <p className="text-sm text-amber-700 mt-1">
                    You forgot to check out
                    {pendingMissedCheckoutRecord?.date && (
                      <> on <strong>{pendingMissedCheckoutRecord.date}</strong></>
                    )}
                    {pendingMissedCheckoutRecord?.location_name && (
                      <> at <strong>{pendingMissedCheckoutRecord.location_name}</strong></>
                    )}
                    . Please provide a justification before checking in today.
                  </p>
                </div>
              </div>

              <textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder="Why did you miss checkout?"
                className="w-full p-3 border border-gray-300 rounded-lg min-h-[100px] text-sm focus:border-[#7C1D3E] focus:ring-1 focus:ring-[#7C1D3E] outline-none"
              />

              <Button
                onClick={submitJustification}
                disabled={justificationLoading || !justification.trim()}
                className="w-full bg-[#7C1D3E] hover:bg-[#6d1729] text-white h-12 text-base"
              >
                {justificationLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Justification'
                )}
              </Button>
            </div>
          )}

          {/* ---------- STATE 3: Not Checked In ---------- */}
          {!isBlocked && !hasPendingMissedCheckout && !isCheckedIn && !todayRecord && (
            <div className="text-center space-y-4 py-4">
              {/* Live clock */}
              <div className="flex items-center justify-center gap-2 text-3xl font-bold text-gray-800 tabular-nums">
                <Clock className="h-7 w-7 text-[#7C1D3E]" />
                {formattedTime}
              </div>

              {/* Greeting */}
              <div>
                <p className="text-lg text-gray-700">
                  {greeting}, <span className="font-semibold">{currentUser.name || 'there'}</span>!
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Mark your attendance for today, {formattedDate}
                </p>
              </div>

              {/* Full Day / Half Day badge */}
              <div className="flex justify-center">
                {!isAfternoon ? (
                  <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2">
                    <Sun className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">Full Day</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-2">
                      <CloudSun className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-700">Half Day</span>
                    </div>
                    <p className="text-xs text-amber-600">
                      Check-in after 1 PM counts as half day
                    </p>
                  </div>
                )}
              </div>

              {/* Check In button */}
              <Button
                onClick={() => setShowCheckInModal(true)}
                className="w-full max-w-xs mx-auto h-14 text-lg font-semibold bg-[#7C1D3E] hover:bg-[#6d1729] text-white rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                <LogIn className="h-5 w-5 mr-2" />
                Check In
              </Button>
            </div>
          )}

          {/* ---------- STATE 4: Checked In (active) ---------- */}
          {!isBlocked && !hasPendingMissedCheckout && isCheckedIn && (
            <div className="space-y-4 py-2">
              {/* Status header */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-3 h-3 bg-green-500 rounded-full" />
                  <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping opacity-75" />
                </div>
                <span className="text-lg font-semibold text-green-700">Checked In</span>
              </div>

              {/* Location & time */}
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="h-4 w-4 text-[#7C1D3E]" />
                <span className="text-sm">
                  {checkedInLocation || 'Unknown location'}
                  {checkInTimeDisplay && (
                    <> since <strong>{checkInTimeDisplay}</strong></>
                  )}
                </span>
              </div>

              {/* Duration counter */}
              {duration && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Duration</p>
                  <p className="text-2xl font-bold text-gray-800 tabular-nums">
                    {String(duration.hours).padStart(2, '0')}:{String(duration.minutes).padStart(2, '0')}
                  </p>
                  {/* Simple progress indicator */}
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-3">
                    <div
                      className="bg-[#7C1D3E] h-1.5 rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min(((duration.hours * 60 + duration.minutes) / 540) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {Math.min(((duration.hours * 60 + duration.minutes) / 540) * 100, 100).toFixed(0)}% of 9h shift
                  </p>
                </div>
              )}

              {/* Check Out button */}
              <Button
                onClick={() => openCheckOutModal()}
                className="w-full h-14 text-lg font-semibold bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                <LogOut className="h-5 w-5 mr-2" />
                Check Out
              </Button>
            </div>
          )}

          {/* ---------- STATE 5: Checked Out (day complete) ---------- */}
          {!isBlocked && !hasPendingMissedCheckout && !isCheckedIn && todayRecord && (
            <div className="text-center space-y-4 py-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-green-800">
                Today's Attendance Complete
              </h2>

              <div className="space-y-2 text-sm text-gray-600">
                {todayRecord.location_name && (
                  <div className="flex items-center justify-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>{todayRecord.location_name}</span>
                  </div>
                )}

                <div className="flex items-center justify-center gap-4">
                  <span>
                    Check-in: <strong>{formatRecordTime(todayRecord.check_in_time)}</strong>
                  </span>
                  <span className="text-gray-300">|</span>
                  <span>
                    Check-out: <strong>{formatRecordTime(todayRecord.check_out_time)}</strong>
                  </span>
                </div>

                {formatHoursWorked(todayRecord) && (
                  <p className="text-gray-700">
                    Hours worked: <strong>{formatHoursWorked(todayRecord)}</strong>
                  </p>
                )}

                {/* Status badge */}
                <div className="flex justify-center pt-1">
                  {todayRecord.status === 'on_time' || todayRecord.hours_worked >= 7 ? (
                    <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Present
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 bg-yellow-100 text-yellow-800 text-xs font-medium px-3 py-1 rounded-full">
                      <CloudSun className="h-3.5 w-3.5" />
                      Half Day
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Loading state for today record check */}
          {!isBlocked && !hasPendingMissedCheckout && !isCheckedIn && !todayRecord && todayRecordLoading && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 text-[#7C1D3E] animate-spin mx-auto" />
              <p className="text-sm text-gray-500 mt-2">Loading attendance status...</p>
            </div>
          )}

        </CardContent>
      </Card>

      {/* ================================================================
          STRIKES SECTION (collapsible)
          ================================================================ */}
      {strikes && strikeCount > 0 && (
        <Card>
          <button
            onClick={() => setStrikesExpanded(!strikesExpanded)}
            className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className={`h-5 w-5 ${strikeCount >= 3 ? 'text-red-500' : strikeCount >= 2 ? 'text-amber-500' : 'text-gray-400'}`} />
              <span className="font-semibold text-gray-800">Strikes</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                strikeCount >= 3 ? 'bg-red-100 text-red-700' :
                strikeCount >= 2 ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {strikeCount}
              </span>
            </div>
            {strikesExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {strikesExpanded && (
            <CardContent className="pt-0 pb-4">
              {/* Strike progress bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{strikeCount} of {strikes.max_strikes || 3} strikes</span>
                  {strikes.is_locked && <span className="text-red-600 font-medium">LOCKED</span>}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      strikeCount >= 3 ? 'bg-red-500' :
                      strikeCount >= 2 ? 'bg-amber-500' :
                      'bg-amber-400'
                    }`}
                    style={{ width: `${Math.min((strikeCount / (strikes.max_strikes || 3)) * 100, 100)}%` }}
                  />
                </div>
              </div>

              {/* Strike history list */}
              {strikes.strike_history && strikes.strike_history.length > 0 ? (
                <div className="space-y-2">
                  {strikes.strike_history.map((strike, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                      <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                        idx === 0 ? 'bg-red-500' : 'bg-gray-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-800">
                          {strike.reason || 'Missed checkout'}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {strike.date || strike.created_at
                            ? format(new Date(strike.date || strike.created_at), 'MMM d, yyyy')
                            : 'Unknown date'
                          }
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No strike history available.</p>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* ================================================================
          BH0 PENALTIES CARD (persistent red card)
          ================================================================ */}
      <div ref={bh0CardRef}>
        {bh0Loading ? (
          <Card className="border-l-4 border-red-500">
            <CardContent className="py-6 text-center">
              <Loader2 className="h-5 w-5 text-red-400 animate-spin mx-auto" />
              <p className="text-sm text-gray-500 mt-2">Loading BH0 penalties...</p>
            </CardContent>
          </Card>
        ) : bh0Penalties.length > 0 ? (
          <Card className="border-l-4 border-red-500">
            <button
              onClick={() => setBh0CardExpanded(!bh0CardExpanded)}
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-red-50/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-red-600" />
                <span className="font-semibold text-gray-800">Billing Hour Zero Penalties</span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                  {bh0ActiveCount} active
                </span>
              </div>
              {bh0CardExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>

            {bh0CardExpanded && (
              <CardContent className="pt-0 pb-4">
                <div className="space-y-3">
                  {bh0Penalties.map((penalty) => (
                    <div
                      key={penalty.id}
                      className={`border rounded-lg p-3 ${
                        penalty.status === 'reversed'
                          ? 'bg-green-50/50 border-green-200'
                          : penalty.status === 'appealed'
                          ? 'bg-amber-50/50 border-amber-200'
                          : 'bg-red-50/50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-800">{penalty.date}</span>
                          {getBh0StatusBadge(penalty.status)}
                        </div>
                        {penalty.status === 'active' && (
                          <button
                            onClick={() => {
                              setBh0AppealingId(bh0AppealingId === penalty.id ? null : penalty.id);
                              setBh0AppealText('');
                            }}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {bh0AppealingId === penalty.id ? 'Cancel' : 'Appeal'}
                          </button>
                        )}
                      </div>

                      <p className="text-xs text-gray-600 mb-1">
                        <span className="font-medium">Reason:</span> {penalty.reason}
                      </p>
                      <p className="text-xs text-gray-500">
                        Marked by {penalty.marked_by_name} ({penalty.marked_by_role})
                      </p>

                      {/* Appeal reason display */}
                      {penalty.appeal_reason && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                          <span className="font-medium text-amber-800">Your appeal:</span>{' '}
                          <span className="text-amber-700">{penalty.appeal_reason}</span>
                        </div>
                      )}

                      {/* Admin decision display */}
                      {penalty.admin_notes && (
                        <div className={`mt-2 p-2 rounded text-xs ${
                          penalty.status === 'reversed'
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-red-50 border border-red-200'
                        }`}>
                          <span className={`font-medium ${
                            penalty.status === 'reversed' ? 'text-green-800' : 'text-red-800'
                          }`}>
                            Admin decision:
                          </span>{' '}
                          <span className={penalty.status === 'reversed' ? 'text-green-700' : 'text-red-700'}>
                            {penalty.admin_notes}
                          </span>
                        </div>
                      )}

                      {/* Inline appeal form */}
                      {bh0AppealingId === penalty.id && (
                        <div className="mt-3 space-y-2">
                          <textarea
                            value={bh0AppealText}
                            onChange={(e) => setBh0AppealText(e.target.value)}
                            placeholder="Explain why this BH0 penalty should be reversed (min 10 characters)..."
                            rows={3}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#7C1D3E]/30 focus:border-[#7C1D3E] outline-none resize-none"
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => submitBh0Appeal(penalty.id)}
                              disabled={bh0AppealLoading || bh0AppealText.trim().length < 10}
                              className="bg-[#7C1D3E] hover:bg-[#6d1729] text-white text-xs h-8"
                              size="sm"
                            >
                              {bh0AppealLoading ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                              ) : (
                                <Shield className="h-3.5 w-3.5 mr-1" />
                              )}
                              Submit Appeal
                            </Button>
                            <span className="text-[10px] text-gray-400">
                              {bh0AppealText.trim().length}/10 min chars
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ) : null}
      </div>

      {/* ================================================================
          ATTENDANCE HISTORY TABS
          ================================================================ */}
      <Card>
        <CardContent className="pt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger
                value="week"
                className="data-[state=active]:bg-[#7C1D3E] data-[state=active]:text-white"
              >
                This Week
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="data-[state=active]:bg-[#7C1D3E] data-[state=active]:text-white"
              >
                History
              </TabsTrigger>
            </TabsList>

            {/* ---------- THIS WEEK tab ---------- */}
            <TabsContent value="week" className="mt-0">
              {weeklyLoading ? (
                <div className="text-center py-6">
                  <Loader2 className="h-6 w-6 text-[#7C1D3E] animate-spin mx-auto" />
                  <p className="text-sm text-gray-500 mt-2">Loading weekly view...</p>
                </div>
              ) : (
                <>
                  {/* Desktop: 7-column grid */}
                  <div className="hidden md:grid grid-cols-7 gap-2">
                    {weekDays.map((day, i) => {
                      const entry = getWeekDayEntry(day);
                      const dayStr = format(day, 'yyyy-MM-dd');
                      const hasBh0 = !!bh0ByDate[dayStr];
                      return (
                        <div
                          key={i}
                          className={`border rounded-lg p-3 min-h-[120px] transition-colors ${
                            hasBh0 ? 'border-red-400 bg-red-50' : getStatusColor(entry, day)
                          }`}
                        >
                          <div className={`font-semibold text-center text-sm ${isToday(day) ? 'text-[#7C1D3E]' : 'text-gray-700'}`}>
                            {format(day, 'EEE')}
                          </div>
                          <div className={`text-xs text-center mb-2 ${isToday(day) ? 'text-[#7C1D3E] font-medium' : 'text-gray-500'}`}>
                            {format(day, 'MMM d')}
                          </div>

                          {isToday(day) && (
                            <div className="flex justify-center mb-1">
                              <span className="text-[10px] font-medium text-[#7C1D3E] bg-[#7C1D3E]/10 px-2 py-0.5 rounded-full">
                                Today
                              </span>
                            </div>
                          )}

                          {/* BH0 label */}
                          {hasBh0 && (
                            <div className="flex justify-center mb-1">
                              <span className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full border border-red-300">
                                BH0
                              </span>
                            </div>
                          )}

                          {entry ? (
                            <div className="text-center space-y-1">
                              <div className="flex justify-center">
                                {entry.regularized ? getRegBadge(format(day, 'yyyy-MM-dd')) || getStatusBadge(entry) : getStatusBadge(entry)}
                              </div>
                              {entry.location_name && (
                                <p className="text-[10px] text-gray-500 truncate" title={entry.location_name}>
                                  {entry.location_name}
                                </p>
                              )}
                              {entry.hours_worked != null && (
                                <p className="text-[10px] text-gray-500">
                                  {entry.hours_worked.toFixed(1)}h
                                </p>
                              )}
                            </div>
                          ) : (
                            !isToday(day) && (
                              <div className="text-center mt-1 space-y-1">
                                {/* Regularization status indicator */}
                                {regByDate[format(day, 'yyyy-MM-dd')] ? (
                                  <div className="flex justify-center">
                                    {getRegBadge(format(day, 'yyyy-MM-dd'))}
                                  </div>
                                ) : canRegularize(day, null) ? (
                                  <>
                                    <p className="text-[10px] text-gray-400">No record</p>
                                    <button
                                      onClick={() => {
                                        setRegDate(format(day, 'yyyy-MM-dd'));
                                        setRegModalOpen(true);
                                      }}
                                      className="text-[10px] text-blue-600 hover:text-blue-800 font-medium hover:underline"
                                    >
                                      Regularize
                                    </button>
                                  </>
                                ) : (
                                  <p className="text-[10px] text-gray-400">
                                    {isBefore(day, startOfDay(new Date())) ? 'No record' : '--'}
                                  </p>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Mobile: Horizontal scroll */}
                  <div className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory md:hidden -mx-2 px-2">
                    {weekDays.map((day, i) => {
                      const entry = getWeekDayEntry(day);
                      const dayStr = format(day, 'yyyy-MM-dd');
                      const hasBh0 = !!bh0ByDate[dayStr];
                      return (
                        <div
                          key={i}
                          className={`flex-shrink-0 w-[110px] snap-center border rounded-lg p-3 min-h-[120px] transition-colors ${
                            hasBh0 ? 'border-red-400 bg-red-50' : getStatusColor(entry, day)
                          }`}
                        >
                          <div className={`font-semibold text-center text-sm ${isToday(day) ? 'text-[#7C1D3E]' : 'text-gray-700'}`}>
                            {format(day, 'EEE')}
                          </div>
                          <div className={`text-xs text-center mb-2 ${isToday(day) ? 'text-[#7C1D3E] font-medium' : 'text-gray-500'}`}>
                            {format(day, 'MMM d')}
                          </div>

                          {isToday(day) && (
                            <div className="flex justify-center mb-1">
                              <span className="text-[10px] font-medium text-[#7C1D3E] bg-[#7C1D3E]/10 px-2 py-0.5 rounded-full">
                                Today
                              </span>
                            </div>
                          )}

                          {/* BH0 label */}
                          {hasBh0 && (
                            <div className="flex justify-center mb-1">
                              <span className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full border border-red-300">
                                BH0
                              </span>
                            </div>
                          )}

                          {entry ? (
                            <div className="text-center space-y-1">
                              <div className="flex justify-center">
                                {entry.regularized ? getRegBadge(format(day, 'yyyy-MM-dd')) || getStatusBadge(entry) : getStatusBadge(entry)}
                              </div>
                              {entry.hours_worked != null && (
                                <p className="text-[10px] text-gray-500">
                                  {entry.hours_worked.toFixed(1)}h
                                </p>
                              )}
                            </div>
                          ) : (
                            !isToday(day) && (
                              <div className="text-center mt-1 space-y-1">
                                {regByDate[format(day, 'yyyy-MM-dd')] ? (
                                  <div className="flex justify-center">
                                    {getRegBadge(format(day, 'yyyy-MM-dd'))}
                                  </div>
                                ) : canRegularize(day, null) ? (
                                  <>
                                    <p className="text-[10px] text-gray-400">No record</p>
                                    <button
                                      onClick={() => {
                                        setRegDate(format(day, 'yyyy-MM-dd'));
                                        setRegModalOpen(true);
                                      }}
                                      className="text-[10px] text-blue-600 hover:text-blue-800 font-medium hover:underline"
                                    >
                                      Regularize
                                    </button>
                                  </>
                                ) : (
                                  <p className="text-[10px] text-gray-400">
                                    {isBefore(day, startOfDay(new Date())) ? 'No record' : '--'}
                                  </p>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Mobile scroll dots */}
                  <div className="flex justify-center gap-1 mt-2 md:hidden">
                    {weekDays.map((day, i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full ${isToday(day) ? 'bg-[#7C1D3E]' : 'bg-gray-300'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </TabsContent>

            {/* ---------- HISTORY tab ---------- */}
            <TabsContent value="history" className="mt-0">
              <AttendanceHistory />

              {/* My Regularization Requests */}
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  <FileEdit className="h-4 w-4 text-blue-600" />
                  My Regularization Requests
                  {myRegRequests.length > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {myRegRequests.length}
                    </span>
                  )}
                </h3>

                {regLoading ? (
                  <div className="text-center py-4">
                    <Loader2 className="h-5 w-5 text-gray-400 animate-spin mx-auto" />
                  </div>
                ) : myRegRequests.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-400 bg-gray-50 rounded-lg">
                    No regularization requests yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {myRegRequests.map((req) => {
                      const statusStyles = {
                        pending_manager: 'bg-amber-50 border-amber-200',
                        pending_admin: 'bg-blue-50 border-blue-200',
                        approved: 'bg-green-50 border-green-200',
                        rejected: 'bg-red-50 border-red-200',
                      };
                      const statusBadges = {
                        pending_manager: <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending Manager</span>,
                        pending_admin: <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Pending Admin</span>,
                        approved: <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Approved</span>,
                        rejected: <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">Rejected</span>,
                      };
                      return (
                        <div
                          key={req.id || req._id || req.date}
                          className={`border rounded-lg p-3 ${statusStyles[req.status] || 'bg-gray-50 border-gray-200'}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-800">{req.date}</span>
                            {statusBadges[req.status] || <span className="text-xs text-gray-500">{req.status}</span>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {req.location_name || 'Unknown'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {req.claimed_check_in} - {req.claimed_check_out}
                            </span>
                          </div>
                          {/* Photo proof thumbnails */}
                          {(req.check_in_proof_url || req.check_out_proof_url || (req.additional_proof_urls || []).length > 0) && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {req.check_in_proof_url && (
                                <div className="text-center">
                                  <p className="text-[10px] text-gray-500 mb-0.5">Check-in</p>
                                  <img
                                    src={(process.env.REACT_APP_BACKEND_URL || '') + req.check_in_proof_url}
                                    alt="Check-in proof"
                                    className="w-12 h-12 rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => window.open((process.env.REACT_APP_BACKEND_URL || '') + req.check_in_proof_url, '_blank')}
                                  />
                                </div>
                              )}
                              {req.check_out_proof_url && (
                                <div className="text-center">
                                  <p className="text-[10px] text-gray-500 mb-0.5">Check-out</p>
                                  <img
                                    src={(process.env.REACT_APP_BACKEND_URL || '') + req.check_out_proof_url}
                                    alt="Check-out proof"
                                    className="w-12 h-12 rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => window.open((process.env.REACT_APP_BACKEND_URL || '') + req.check_out_proof_url, '_blank')}
                                  />
                                </div>
                              )}
                              {(req.additional_proof_urls || []).map((url, i) => (
                                <div key={i} className="text-center">
                                  <p className="text-[10px] text-gray-500 mb-0.5">Evidence {i + 1}</p>
                                  <img
                                    src={(process.env.REACT_APP_BACKEND_URL || '') + url}
                                    alt={`Evidence ${i + 1}`}
                                    className="w-12 h-12 rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => window.open((process.env.REACT_APP_BACKEND_URL || '') + url, '_blank')}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          {req.status === 'rejected' && req.rejection_reason && (
                            <p className="text-xs text-red-600 mt-1.5 bg-red-100 rounded px-2 py-1">
                              Reason: {req.rejection_reason}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Team Strike Summary - Manager/Admin only */}
              {isManager && teamStrikes.length > 0 && (
                <div className="mt-6">
                  <TeamStrikesTable
                    teamStrikes={teamStrikes}
                    loading={teamStrikesLoading}
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Regularization Modal */}
      <DailyRegularizationModal
        isOpen={regModalOpen}
        onClose={() => setRegModalOpen(false)}
        selectedDate={regDate}
        onSuccess={() => {
          fetchMyRegularizations();
          // Also refresh weekly summary
          setWeeklySummary([]);
        }}
      />
    </div>
  );
}
